import React, { useState } from 'react';
import { useLang } from '../../contexts/LanguageContext';
import { secureStorage } from '../../utils/secureStorage';
import toast from '../../utils/toast';
import { BACKEND_URL } from '../../utils/backendUrl';

const INTEGRATION_TYPES = [
  { value: 'email_smtp', label: 'Email (SMTP)', icon: '📧' },
  { value: 'email_gmail', label: 'Email (Gmail OAuth)', icon: '📧' },
  { value: 'email_sendgrid', label: 'Email (SendGrid)', icon: '📧' },
  { value: 'whatsapp', label: 'WhatsApp Business', icon: '💬' },
  { value: 'telegram', label: 'Telegram Bot', icon: '✈️' },
  { value: 'webhook', label: 'Webhook', icon: '🔗' },
];

const IntegrationSetupWizard = ({ onClose, onSuccess }) => {
  const t = useLang();
  const [currentStep, setCurrentStep] = useState(1);
  const [integrationType, setIntegrationType] = useState('');
  const [config, setConfig] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/integrations/test`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            integration_type: integrationType,
            config
          })
        }
      );

      const result = await response.json();
      setTestResult(result);
    } catch (error) {
      setTestResult({ success: false, message: t('admin.error.networkError') });
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);

    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/integrations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            integration_type: integrationType,
            config,
            notification_mappings: [],
            is_active: true,
            initial_status: testResult?.success ? 'connected' : 'disconnected'
          })
        }
      );

      if (response.ok) {
        toast.success(t('admin.integrations.created'));
        onSuccess();
      } else {
        toast.error(t('admin.integrations.createFailed'));
      }
    } catch (error) {
      toast.error(t('admin.error.networkError'));
    } finally {
      setSaving(false);
    }
  };

  const renderConfigForm = () => {
    switch (integrationType) {
      case 'email_smtp':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                SMTP Host *
              </label>
              <input
                type="text"
                value={config.host || ''}
                onChange={(e) => setConfig({ ...config, host: e.target.value })}
                placeholder="smtp.gmail.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Port *
              </label>
              <input
                type="number"
                value={config.port || ''}
                onChange={(e) => setConfig({ ...config, port: parseInt(e.target.value) })}
                placeholder="587"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Username *
              </label>
              <input
                type="text"
                value={config.username || ''}
                onChange={(e) => setConfig({ ...config, username: e.target.value })}
                placeholder="admin@emzi.digital"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Password *
              </label>
              <input
                type="password"
                value={config.password || ''}
                onChange={(e) => setConfig({ ...config, password: e.target.value })}
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
        );

      case 'whatsapp':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                API Token *
              </label>
              <input
                type="password"
                value={config.api_token || ''}
                onChange={(e) => setConfig({ ...config, api_token: e.target.value })}
                placeholder="EAAx..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Phone Number ID *
              </label>
              <input
                type="text"
                value={config.phone_number_id || ''}
                onChange={(e) => setConfig({ ...config, phone_number_id: e.target.value })}
                placeholder="123456789"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Business Account ID *
              </label>
              <input
                type="text"
                value={config.business_account_id || ''}
                onChange={(e) => setConfig({ ...config, business_account_id: e.target.value })}
                placeholder="987654321"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
        );

      case 'telegram':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Bot Token *
              </label>
              <input
                type="password"
                value={config.bot_token || ''}
                onChange={(e) => setConfig({ ...config, bot_token: e.target.value })}
                placeholder="123456:ABC-DEF..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                💡 Get your bot token from @BotFather on Telegram
              </p>
            </div>
          </div>
        );

      case 'webhook':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Webhook URL *
              </label>
              <input
                type="url"
                value={config.url || ''}
                onChange={(e) => setConfig({ ...config, url: e.target.value })}
                placeholder="https://example.com/webhook"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Shared Secret *
              </label>
              <input
                type="password"
                value={config.shared_secret || ''}
                onChange={(e) => setConfig({ ...config, shared_secret: e.target.value })}
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
        );

      default:
        return <p className="text-sm text-gray-500">{t('admin.integrations.selectType')}</p>;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 mb-4">
              {t('admin.integrations.selectIntegrationType')}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {INTEGRATION_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setIntegrationType(type.value)}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    integrationType === type.value
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  <div className="text-2xl mb-1">{type.icon}</div>
                  <div className="text-sm font-semibold text-gray-900">{type.label}</div>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="bg-orange-50 border-l-4 border-orange-500 p-3 rounded-r-lg">
              <p className="text-sm text-orange-800">
                🔐 {t('admin.integrations.credentialsSecure')}
              </p>
            </div>
            {renderConfigForm()}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="text-4xl mb-2">🧪</div>
              <h3 className="text-lg font-bold text-gray-900">
                {t('admin.integrations.testConnection')}
              </h3>
              <p className="text-sm text-gray-600 mt-2">
                {t('admin.integrations.testBeforeSave')}
              </p>
            </div>

            {testResult && (
              <div className={`border-l-4 rounded-r-lg p-3 ${
                testResult.success
                  ? 'bg-green-50 border-green-500'
                  : 'bg-red-50 border-red-500'
              }`}>
                <p className={`text-sm ${
                  testResult.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {testResult.message}
                </p>
              </div>
            )}

            <button
              onClick={handleTest}
              disabled={testing}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {testing ? (
                <>
                  <span className="animate-spin">⏳</span>
                  {t('admin.integrations.testing')}...
                </>
              ) : (
                <>
                  🧪 {t('admin.integrations.testNow')}
                </>
              )}
            </button>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="text-center py-2">
              <div className="text-4xl mb-2">✅</div>
              <h3 className="text-lg font-bold text-gray-900">
                {t('admin.integrations.reviewTitle')}
              </h3>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <div>
                <span className="text-gray-600">{t('admin.integrations.type')}:</span>
                <span className="font-semibold text-gray-900 ml-2">
                  {INTEGRATION_TYPES.find(t => t.value === integrationType)?.label}
                </span>
              </div>
              <div>
                <span className="text-gray-600">{t('admin.integrations.status')}:</span>
                <span className={`font-semibold ml-2 ${
                  testResult?.success ? 'text-green-700' : 'text-orange-700'
                }`}>
                  {testResult?.success ? '✅ Tested' : '⚠️ Not tested'}
                </span>
              </div>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-r-lg">
              <p className="text-sm text-green-800">
                🔒 {t('admin.audit.willBeLogged')}
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold">
                {t('admin.integrations.setupWizard')}
              </h2>
              <p className="text-sm text-orange-100 mt-1">
                {t('admin.functionalGroups.step')} {currentStep} {t('admin.functionalGroups.of')} 4
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="bg-gray-200 h-1">
          <div
            className="bg-orange-500 h-1 transition-all duration-300"
            style={{ width: `${(currentStep / 4) * 100}%` }}
          />
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {renderStep()}
        </div>

        <div className="border-t border-gray-200 p-4 flex justify-between gap-2">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← {t('common.back')}
          </button>

          {currentStep < 4 ? (
            <button
              onClick={handleNext}
              disabled={currentStep === 1 && !integrationType}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {t('common.next')} →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {saving ? t('common.saving') : `✓ ${t('admin.integrations.saveIntegration')}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntegrationSetupWizard;
